// modules/facturacion/components/CrearPrefacturaModal.jsx
import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Alert, CircularProgress,
  Box, Divider, Grid, Typography, Checkbox,
  Chip, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper,
} from "@mui/material";
import * as svc from "../services/facturacionService";

const MESES_NUM = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];
const yearActual = new Date().getFullYear();
const mesActual  = new Date().getMonth() + 1;

const CrearPrefacturaModal = ({ open, onClose, onSuccess }) => {
  // Paso: 1=seleccionar cortes, 2=datos factura, 3=confirmar
  const [paso,     setPaso]     = useState(1);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  // Paso 1 — cortes confirmados
  const [filtroMes,   setFiltroMes]   = useState(mesActual);
  const [filtroYear,  setFiltroYear]  = useState(yearActual);
  const [cortes,      setCortes]      = useState([]);
  const [loadCortes,  setLoadCortes]  = useState(false);
  const [selCortes,   setSelCortes]   = useState([]);
  const [tarifas,     setTarifas]     = useState({});

  // Paso 2 — datos de la factura
  const [infoLegales, setInfoLegales]   = useState([]);
  const [codCliente,  setCodCliente]    = useState("");
  const [form, setForm] = useState({
    id_legal          : "",
    nit               : "",
    fac_Nombre        : "",
    mes               : "",
    year              : yearActual,
    sucursal          : "La Paz",
    codigoMoneda      : 1,
    tipo              : "TRS",
  });

  // Buscar cortes confirmados
  const buscarCortes = async () => {
    setLoadCortes(true);
    setError(null);
    try {
      const { data } = await svc.getCortes({
        mes   : filtroMes,
        year  : filtroYear,
        estado: "Confirmado",
      });
      setCortes(Array.isArray(data) ? data : []);
      setSelCortes([]);
    } catch {
      setError("Error al cargar los cortes confirmados");
    } finally {
      setLoadCortes(false);
    }
  };

  // Cargar tarifas vigentes de los cortes seleccionados
  const cargarTarifas = async (cortesSeleccionados) => {
    const fecha = `${filtroYear}-${String(filtroMes).padStart(2, "0")}-01`;
    const result = {};
    await Promise.all(
      cortesSeleccionados.map(async (c) => {
        try {
          const { data } = await svc.getTarifaVigente({
            cod_servicio_cliente: c.cod_servicio_cliente,
            id_fac_item         : c.id_fac_item,
            fecha,
          });
          result[c.id_corte] = data;
        } catch {
          result[c.id_corte] = null;
        }
      })
    );
    setTarifas(result);
  };

  const toggleCorte = (corte) => {
    setSelCortes((prev) => {
      const existe = prev.find((c) => c.id_corte === corte.id_corte);
      return existe ? prev.filter((c) => c.id_corte !== corte.id_corte) : [...prev, corte];
    });
  };

  // Ir al paso 2
  const irPaso2 = async () => {
    if (selCortes.length === 0) return setError("Seleccione al menos un corte");

    setError(null);
    setLoading(true);
    try {
      // Cargar tarifas y obtener cod_cliente del servicio
      await cargarTarifas(selCortes);

      // Cargar cliente del servicio para obtener el info legal
      // Nota: para obtener cod_cliente del servicio, usamos el servicio de clientes
      const mesLabel = MESES_NUM.find((m) => m.value === Number(filtroMes))?.label || "";
      setForm((p) => ({
        ...p,
        mes : mesLabel,
        year: filtroYear,
      }));

      setPaso(2);
    } catch {
      setError("Error al preparar los datos");
    } finally {
      setLoading(false);
    }
  };

  // Cargar info legal cuando se ingresa el cod_cliente
  const cargarInfoLegal = async (cod) => {
    if (!cod?.trim()) return;
    try {
      const { data } = await svc.getInfoLegal(cod.trim());
      setInfoLegales(Array.isArray(data) ? data : []);
    } catch {
      setInfoLegales([]);
    }
  };

  const handleSelectLegal = (id_legal) => {
    const legal = infoLegales.find((l) => l.id_legal === Number(id_legal));
    if (legal) {
      setForm((p) => ({
        ...p,
        id_legal  : id_legal,
        nit       : legal.nit,
        fac_Nombre: legal.nombre_facturar,
      }));
    }
  };

  // Construir detalles desde cortes seleccionados + tarifas
  const buildDetalles = () => {
    return selCortes.map((c) => {
      const tarifa = tarifas[c.id_corte];
      return {
        id_fac_item     : c.id_fac_item,
        id_corte        : c.id_corte,
        precio_unitario : tarifa?.precio_unitario || 0,
        cantidad        : c.total_items,
        detalle         : c.concepto || `Concepto #${c.id_fac_item}`,
        tipo            : c.id_fac_item,
        mes_servicio    : form.mes,
        year            : String(form.year),
        descuento_interno: 0,
      };
    });
  };

  const totalFactura = () => {
    return selCortes.reduce((acc, c) => {
      const tarifa = tarifas[c.id_corte];
      return acc + (c.total_items * (tarifa?.precio_unitario || 0));
    }, 0);
  };

  // Crear prefactura
  const handleSubmit = async () => {
    if (!form.nit)       return setError("El NIT es requerido");
    if (!form.fac_Nombre) return setError("El nombre a facturar es requerido");
    if (!form.sucursal)  return setError("La sucursal es requerida");

    setLoading(true);
    setError(null);
    try {
      const detalles = buildDetalles();
      await svc.crearPrefactura({
        id_servicio_cliente: selCortes[0]?.cod_servicio_cliente,
        ...form,
        id_legal: form.id_legal || null,
        detalles,
      });
      onSuccess();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al crear la prefactura");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Nueva Prefactura
        {paso > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Paso {paso} de 2
          </Typography>
        )}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* ── PASO 1: Seleccionar cortes ── */}
          {paso === 1 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Selecciona los cortes confirmados a incluir en la prefactura
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  select size="small" label="Mes" sx={{ minWidth: 140 }}
                  value={filtroMes}
                  onChange={(e) => setFiltroMes(e.target.value)}
                >
                  {MESES_NUM.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small" label="Año" type="number" sx={{ width: 100 }}
                  value={filtroYear}
                  onChange={(e) => setFiltroYear(e.target.value)}
                />
                <Button variant="outlined" size="small" onClick={buscarCortes}
                  disabled={loadCortes}>
                  {loadCortes ? <CircularProgress size={16} /> : "Buscar"}
                </Button>
              </Box>

              {cortes.length === 0 ? (
                <Alert severity="info">
                  No hay cortes confirmados para el mes/año seleccionado.
                  Confirme los cortes en la pestaña "Cortes mensuales" antes de facturar.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Servicio</TableCell>
                        <TableCell>Concepto</TableCell>
                        <TableCell align="center">Items</TableCell>
                        <TableCell>Generado por</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cortes.map((c) => {
                        const sel = !!selCortes.find((s) => s.id_corte === c.id_corte);
                        return (
                          <TableRow key={c.id_corte} hover
                            onClick={() => toggleCorte(c)}
                            sx={{ cursor: "pointer", bgcolor: sel ? "action.selected" : "inherit" }}>
                            <TableCell padding="checkbox">
                              <Checkbox checked={sel} size="small" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {c.servicio_descripcion || `#${c.cod_servicio_cliente}`}
                              </Typography>
                            </TableCell>
                            <TableCell>{c.concepto}</TableCell>
                            <TableCell align="center">
                              <Chip label={c.total_items} size="small" />
                            </TableCell>
                            <TableCell>{c.generado_por}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* ── PASO 2: Datos de la factura ── */}
          {paso === 2 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Datos de facturación — {selCortes.length} corte(s) seleccionado(s)
              </Typography>

              <Grid container spacing={2}>
                {/* Cod cliente para buscar NITs */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      fullWidth size="small" label="Código del cliente"
                      value={codCliente}
                      onChange={(e) => setCodCliente(e.target.value)}
                      helperText="Ingresa el código para cargar los NITs disponibles"
                    />
                    <Button variant="outlined" size="small" sx={{ minWidth: 100 }}
                      onClick={() => cargarInfoLegal(codCliente)}>
                      Buscar NITs
                    </Button>
                  </Box>
                </Grid>

                {infoLegales.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      select fullWidth size="small" label="NIT / Nombre a facturar"
                      value={form.id_legal}
                      onChange={(e) => handleSelectLegal(e.target.value)}
                    >
                      <MenuItem value="">Seleccionar...</MenuItem>
                      {infoLegales.map((l) => (
                        <MenuItem key={l.id_legal} value={l.id_legal}>
                          {l.nit} — {l.nombre_facturar}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth size="small" label="NIT"
                    value={form.nit}
                    onChange={(e) => setForm((p) => ({ ...p, nit: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField fullWidth size="small" label="Nombre a Facturar"
                    value={form.fac_Nombre}
                    onChange={(e) => setForm((p) => ({ ...p, fac_Nombre: e.target.value }))}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    select fullWidth size="small" label="Sucursal"
                    value={form.sucursal}
                    onChange={(e) => setForm((p) => ({ ...p, sucursal: e.target.value }))}
                  >
                    {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    select fullWidth size="small" label="Moneda"
                    value={form.codigoMoneda}
                    onChange={(e) => setForm((p) => ({ ...p, codigoMoneda: e.target.value }))}
                  >
                    <MenuItem value={1}>Bolivianos</MenuItem>
                    <MenuItem value={2}>Dólares</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField fullWidth size="small" label="Mes facturación"
                    value={form.mes} disabled />
                </Grid>

                {/* Preview de líneas */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Líneas de la prefactura
                    </Typography>
                  </Divider>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Concepto</TableCell>
                          <TableCell align="center">Cantidad</TableCell>
                          <TableCell align="right">P.U.</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selCortes.map((c) => {
                          const tarifa = tarifas[c.id_corte];
                          const pu     = tarifa?.precio_unitario || 0;
                          return (
                            <TableRow key={c.id_corte}>
                              <TableCell>{c.concepto}</TableCell>
                              <TableCell align="center">{c.total_items}</TableCell>
                              <TableCell align="right">
                                {tarifa ? Number(pu).toFixed(2) : (
                                  <Chip label="Sin tarifa" size="small" color="error" />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {Number(c.total_items * pu).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            <Typography fontWeight={600}>Total</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600} color="primary">
                              {Number(totalFactura()).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {paso > 1 && (
            <Button onClick={() => { setError(null); setPaso(1); }} disabled={loading}>
              Atrás
            </Button>
          )}
          {paso === 1 ? (
            <Button variant="contained" onClick={irPaso2}
              disabled={loading || selCortes.length === 0}>
              {loading ? <CircularProgress size={16} /> : "Siguiente"}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}>
              {loading ? "Creando..." : "Crear Prefactura"}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CrearPrefacturaModal;